import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { flare } from '@flareapp/js';

const SENSITIVE_KEYS = ['password', 'senha', 'token', 'authorization', 'secret', 'cpf', 'cnpj'];
const SENSITIVE_URL_PATTERNS = [/login/i, /auth/i, /senha/i, /password/i];
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const MAX_BODY_LENGTH = 5000;

const REPORT_DEBOUNCE_TTL_MS = 2 * 60 * 60 * 1000;
const DEBOUNCED_STATUSES = new Set([0, 429]);
const reportedDebouncedEndpoints = new Map<string, number>();

@Injectable()
export class FlareHttpInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (this.shouldReport(err, req.method, req.urlWithParams)) {
          flare.reportMessage(
            `HTTP ${err.status || 0} ${req.method} ${req.urlWithParams}`,
            {
              status: err.status,
              statusText: err.statusText,
              method: req.method,
              url: req.urlWithParams,
              message: err.message,
              requestBody: this.serializeBody(req.body, req.urlWithParams),
              responseBody: this.serializeBody(err.error, req.urlWithParams),
            },
            'HttpError',
          );
        }
        return throwError(() => err);
      }),
    );
  }

  private shouldReport(err: HttpErrorResponse, method: string, url: string): boolean {
    if (this.isDeviceRegistryToleratedError(err.status, url)) return false;
    if (err.status === 0 && typeof navigator !== 'undefined' && navigator.onLine === false) {
      return false;
    }
    if (DEBOUNCED_STATUSES.has(err.status)) {
      const key = `${err.status} ${method} ${this.getPath(url)}`;
      const now = Date.now();
      const lastReportedAt = reportedDebouncedEndpoints.get(key);
      if (lastReportedAt != null && now - lastReportedAt < REPORT_DEBOUNCE_TTL_MS) return false;
      reportedDebouncedEndpoints.set(key, now);
      return true;
    }
    if (err.status >= 500) return true;
    return false;
  }

  private isDeviceRegistryToleratedError(status: number, url: string): boolean {
    if (!this.isDeviceRegistryUrl(url)) return false;
    return status === 0 || status === 404 || status === 419;
  }

  private isDeviceRegistryUrl(url: string): boolean {
    return url.includes('/devices/register') || url.includes('/devices/sync');
  }

  private getPath(url: string): string {
    try {
      return new URL(url).pathname;
    } catch {
      const noQuery = url.split('?')[0];
      const schemeIdx = noQuery.indexOf('://');
      if (schemeIdx === -1) return noQuery;
      const pathIdx = noQuery.indexOf('/', schemeIdx + 3);
      return pathIdx === -1 ? '/' : noQuery.slice(pathIdx);
    }
  }

  private serializeBody(body: any, url: string): any {
    if (body == null) return null;
    if (this.isSensitiveUrl(url)) return '[redacted: sensitive endpoint]';
    if (body instanceof FormData) return '[FormData]';
    if (body instanceof Blob) return '[Blob]';
    if (body instanceof ArrayBuffer) return '[ArrayBuffer]';

    try {
      let toRedact = body;
      if (typeof body === 'string') {
        try {
          toRedact = JSON.parse(body);
        } catch {
          // não é JSON, mantém como string
        }
      }
      const redacted = this.redact(toRedact);
      const serialized = typeof redacted === 'string' ? redacted : JSON.stringify(redacted);
      return serialized.length > MAX_BODY_LENGTH
        ? serialized.slice(0, MAX_BODY_LENGTH) + '...[truncated]'
        : serialized;
    } catch {
      return '[unserializable]';
    }
  }

  private isSensitiveUrl(url: string): boolean {
    return SENSITIVE_URL_PATTERNS.some(p => p.test(url));
  }

  private redact(value: any): any {
    if (value == null || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map(v => this.redact(v));
    const out: Record<string, any> = {};
    for (const k of Object.keys(value)) {
      if (UNSAFE_KEYS.has(k)) continue;
      if (SENSITIVE_KEYS.some(s => k.toLowerCase().includes(s))) {
        out[k] = '[redacted]';
      } else {
        out[k] = this.redact(value[k]);
      }
    }
    return out;
  }
}
